import { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { 
    Settings, Save, Loader2, HardDrive, FileText, MessageSquare, 
    Shield, Users, AlertTriangle, Lock
} from 'lucide-react';
import { toast } from 'sonner';

const AdminSettingsPage = () => {
    const { api, isSuperAdmin } = useAdmin();
    const [settings, setSettings] = useState({
        default_storage_mb: 100,
        default_report_limit: 10,
        default_ai_limit: 50,
        maintenance_mode: false,
        allow_registration: true
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
    const [changingPwd, setChangingPwd] = useState(false);

    const handleChangePassword = async () => {
        if (!pwd.current || !pwd.next) { toast.error('أدخل كلمة المرور الحالية والجديدة'); return; }
        if (pwd.next !== pwd.confirm) { toast.error('تأكيد كلمة المرور غير مطابق'); return; }
        if (pwd.next.length < 8 || !/[A-Z]/.test(pwd.next) || !/[a-z]/.test(pwd.next) || !/[0-9]/.test(pwd.next)) {
            toast.error('كلمة المرور: 8 خانات على الأقل وتحتوي حرفاً كبيراً وصغيراً ورقماً');
            return;
        }
        setChangingPwd(true);
        try {
            await api.post('/admin/change-password', { current_password: pwd.current, new_password: pwd.next });
            toast.success('تم تغيير كلمة المرور بنجاح — استخدمها في الدخول القادم');
            setPwd({ current: '', next: '', confirm: '' });
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : 'تعذر تغيير كلمة المرور');
        } finally {
            setChangingPwd(false);
        }
    };

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await api.get('/admin/settings');
                if (response.data && Object.keys(response.data).length > 0) {
                    setSettings(prev => ({ ...prev, ...response.data }));
                }
            } catch (error) {
                console.error('Failed to fetch settings:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, [api]);

    const handleSave = async () => {
        if (!isSuperAdmin) {
            toast.error('Only super admins can modify settings');
            return;
        }

        setSaving(true);
        try {
            await api.put('/admin/settings', settings);
            toast.success('Settings saved successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-settings-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">System Settings</h1>
                    <p className="text-slate-400 mt-1">Configure platform defaults and features</p>
                </div>
                {isSuperAdmin && (
                    <Button 
                        onClick={handleSave} 
                        disabled={saving}
                        className="bg-teal-500 hover:bg-teal-600"
                        data-testid="save-settings-btn"
                    >
                        {saving ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                        ) : (
                            <><Save className="w-4 h-4 mr-2" /> Save Changes</>
                        )}
                    </Button>
                )}
            </div>

            {!isSuperAdmin && (
                <Card className="border-amber-500/50 bg-amber-500/10">
                    <CardContent className="p-4 flex items-center gap-3">
                        <Shield className="w-5 h-5 text-amber-400" />
                        <p className="text-amber-200 text-sm">
                            Only super admins can modify system settings. You have read-only access.
                        </p>
                    </CardContent>
                </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Default Limits */}
                <Card className="border-slate-700 bg-slate-800/50">
                    <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <Settings className="w-5 h-5 text-teal-400" />
                            Default User Limits
                        </CardTitle>
                        <CardDescription className="text-slate-400">
                            Default limits applied to new users without a subscription
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label className="text-slate-300 flex items-center gap-2">
                                <HardDrive className="w-4 h-4 text-slate-500" />
                                Default Storage (MB)
                            </Label>
                            <Input
                                type="number"
                                value={settings.default_storage_mb}
                                onChange={(e) => setSettings(prev => ({ ...prev, default_storage_mb: parseInt(e.target.value) || 0 }))}
                                disabled={!isSuperAdmin}
                                className="bg-slate-700/50 border-slate-600 text-white disabled:opacity-50"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-slate-300 flex items-center gap-2">
                                <FileText className="w-4 h-4 text-slate-500" />
                                Default Report Limit (per month)
                            </Label>
                            <Input
                                type="number"
                                value={settings.default_report_limit}
                                onChange={(e) => setSettings(prev => ({ ...prev, default_report_limit: parseInt(e.target.value) || 0 }))}
                                disabled={!isSuperAdmin}
                                className="bg-slate-700/50 border-slate-600 text-white disabled:opacity-50"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-slate-300 flex items-center gap-2">
                                <MessageSquare className="w-4 h-4 text-slate-500" />
                                Default AI Chat Limit (per month)
                            </Label>
                            <Input
                                type="number"
                                value={settings.default_ai_limit}
                                onChange={(e) => setSettings(prev => ({ ...prev, default_ai_limit: parseInt(e.target.value) || 0 }))}
                                disabled={!isSuperAdmin}
                                className="bg-slate-700/50 border-slate-600 text-white disabled:opacity-50"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Platform Controls */}
                <Card className="border-slate-700 bg-slate-800/50">
                    <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <Shield className="w-5 h-5 text-teal-400" />
                            Platform Controls
                        </CardTitle>
                        <CardDescription className="text-slate-400">
                            Enable or disable platform features
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                                <Label className="text-slate-300 flex items-center gap-2">
                                    <Users className="w-4 h-4 text-slate-500" />
                                    Allow Registration
                                </Label>
                                <p className="text-xs text-slate-500">
                                    Enable or disable new user registrations
                                </p>
                            </div>
                            <Switch
                                checked={settings.allow_registration}
                                onCheckedChange={(v) => setSettings(prev => ({ ...prev, allow_registration: v }))}
                                disabled={!isSuperAdmin}
                            />
                        </div>

                        <div className="border-t border-slate-700 pt-6">
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label className="text-red-400 flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4" />
                                        Maintenance Mode
                                    </Label>
                                    <p className="text-xs text-slate-500">
                                        Show maintenance page to all non-admin users
                                    </p>
                                </div>
                                <Switch
                                    checked={settings.maintenance_mode}
                                    onCheckedChange={(v) => setSettings(prev => ({ ...prev, maintenance_mode: v }))}
                                    disabled={!isSuperAdmin}
                                />
                            </div>
                            {settings.maintenance_mode && (
                                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                                    <p className="text-red-400 text-sm flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4" />
                                        Maintenance mode is currently active. Users cannot access the platform.
                                    </p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* System Info */}
                <Card className="border-slate-700 bg-slate-800/50 lg:col-span-2">
                    <CardHeader>
                        <CardTitle className="text-lg text-white">System Information</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-slate-700/50 rounded-lg p-4">
                                <p className="text-sm text-slate-400">Platform Version</p>
                                <p className="text-xl font-bold text-white mt-1">1.0.0</p>
                            </div>
                            <div className="bg-slate-700/50 rounded-lg p-4">
                                <p className="text-sm text-slate-400">Environment</p>
                                <p className="text-xl font-bold text-white mt-1">Production</p>
                            </div>
                            <div className="bg-slate-700/50 rounded-lg p-4">
                                <p className="text-sm text-slate-400">AI Provider</p>
                                <p className="text-xl font-bold text-white mt-1">Emergent</p>
                            </div>
                            <div className="bg-slate-700/50 rounded-lg p-4">
                                <p className="text-sm text-slate-400">Database</p>
                                <p className="text-xl font-bold text-white mt-1">MongoDB</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Change Password */}
                <Card className="border-slate-700 bg-slate-800/50 lg:col-span-2" data-testid="admin-change-password-card">
                    <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <Lock className="w-5 h-5 text-teal-400" />
                            Change Password (تغيير كلمة المرور)
                        </CardTitle>
                        <CardDescription className="text-slate-400">
                            8 خانات على الأقل، وتحتوي حرفاً كبيراً وحرفاً صغيراً ورقماً
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label className="text-slate-300">Current password (الحالية)</Label>
                                <Input type="password" dir="ltr" value={pwd.current}
                                    onChange={(e) => setPwd(p => ({ ...p, current: e.target.value }))}
                                    className="bg-slate-700/50 border-slate-600 text-white"
                                    data-testid="admin-current-password-input" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-slate-300">New password (الجديدة)</Label>
                                <Input type="password" dir="ltr" value={pwd.next}
                                    onChange={(e) => setPwd(p => ({ ...p, next: e.target.value }))}
                                    className="bg-slate-700/50 border-slate-600 text-white"
                                    data-testid="admin-new-password-input" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-slate-300">Confirm (تأكيد الجديدة)</Label>
                                <Input type="password" dir="ltr" value={pwd.confirm}
                                    onChange={(e) => setPwd(p => ({ ...p, confirm: e.target.value }))}
                                    className="bg-slate-700/50 border-slate-600 text-white"
                                    data-testid="admin-confirm-password-input" />
                            </div>
                        </div>
                        <Button onClick={handleChangePassword} disabled={changingPwd}
                            className="mt-4 bg-teal-500 hover:bg-teal-600" data-testid="admin-change-password-btn">
                            {changingPwd ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                            Change Password
                        </Button>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default AdminSettingsPage;
